import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Table from "../../../../ui/Table";
import Loader from "../../../../ui/Loader";
import Modal from "../../../../ui/Modal";
import { swalError, swalSuccess } from "../../../../../utils/swalAlert";
import Navbar from "../../../../ui/Navbar";
import { fetchWithAuth } from "../../../../../utils/api";

export default function ViewByQuestion() {
  const [questionNumbers, setQuestionNumbers] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [activeQuestionNumber, setActiveQuestionNumber] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditingQuestion, setCurrentEditingQuestion] = useState(null);
  const [editForm, setEditForm] = useState({
    questionNumber: "",
    obtainedScore: "",
    maxScore: "",
    feedback: "",
    isManualReview: false,
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [fetchingQuestionDetails, setFetchingQuestionDetails] = useState(false);

  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const pathSegments = window.location.pathname.split("/");
  const testId = pathSegments[pathSegments.length - 1];

  const [classInfo, setClassInfo] = useState({ id: null, name: "Loading..." });
  const [testInfo, setTestInfo] = useState({ id: null, name: "Loading..." });

  useEffect(() => {
    fetchWithAuth(`${serverUrl}/api/breadcrumbs/get?testId=${testId}`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data.class.id) {
          setClassInfo({ name: data.class.name, id: data.class.id });
        } else {
          setClassInfo({ id: null, name: "Unknown Class" });
        }
        if (data.test.id) {
          setTestInfo({ name: data.test.name, id: data.test.id });
        } else {
          setTestInfo({ id: null, name: "Unknown Test" });
        }
      })
      .catch((err) => {
        setClassInfo({ id: null, name: "Unknown Class" });
      });
  }, [testId, serverUrl]);

  const breadcrumbs = [
    { name: "Home", path: "/instructor" },
    {
      name: `${classInfo.name}`,
      path: `/instructor/manage-class/${classInfo.id}`,
    },
    {
      name: `${testInfo.name}`,
      path: `/instructor/test-grading/${testInfo.id}`,
    },
    {
      name: "View by Question",
      path: `/instructor/view-by-question/${testId}`,
      isLast: true,
    },
  ];

  const handlePreviewClick = (uri) => {
    if (uri && uri !== "") {
      window.open(uri, "_blank");
    } else {
      swalError("File not available");
    }
  };

  const handleEditClick = async (question) => {
    // Open modal immediately and show loader
    setIsModalOpen(true);
    setFetchingQuestionDetails(true);
    setCurrentEditingQuestion(question);
    setEditForm({
      questionNumber: question.questionNumber,
      obtainedScore: question.obtainedScore,
      maxScore: question.maxScore,
      feedback: question.feedback,
      isManualReview: question.isManualReview || false,
    });

    // Fetch detailed question information to get the questionId
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/get/${question.answerPaperId}`,
        {
          credentials: "include",
        }
      );
      if (!response) {
        setIsModalOpen(false);
        return;
      }
      const data = await response.json();

      if (!response.ok) {
        throw new Error("Failed to fetch question details");
      }

      // Find the specific question from the result array
      const questionDetail = data.score.result.find(
        (q) => q.questionNumber === question.questionNumber
      );

      if (questionDetail) {
        // Update the current editing question with the questionId
        setCurrentEditingQuestion({
          ...question,
          _id: questionDetail._id, // This is the questionId we need
        });
        setIsSubmitEnabled(false);
      } else {
        throw new Error("Question not found");
      }
    } catch (error) {
      swalError("Failed to load question details for editing");
      setIsModalOpen(false);
      return;
    } finally {
      setFetchingQuestionDetails(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, type, value, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setEditForm({
      ...editForm,
      [name]: newValue,
    });

    // Enable submit if changes are made
    if (currentEditingQuestion) {
      const isQuestionNumberChanged =
        newValue !== currentEditingQuestion.questionNumber &&
        name === "questionNumber";
      const isObtainedScoreChanged =
        parseFloat(newValue) !== currentEditingQuestion.obtainedScore &&
        name === "obtainedScore";
      const isMaxScoreChanged =
        parseFloat(newValue) !== currentEditingQuestion.maxScore &&
        name === "maxScore";
      const isFeedbackChanged =
        newValue !== currentEditingQuestion.feedback && name === "feedback";
      const isManualReviewChanged =
        (name === "isManualReview" ? newValue : editForm.isManualReview) !==
        (currentEditingQuestion.isManualReview || false);

      setIsSubmitEnabled(
        isQuestionNumberChanged ||
          isObtainedScoreChanged ||
          isMaxScoreChanged ||
          isFeedbackChanged ||
          isManualReviewChanged
      );
    }
  };

  // Effect to check if form has changes
  useEffect(() => {
    if (currentEditingQuestion) {
      const isQuestionNumberChanged =
        editForm.questionNumber !== currentEditingQuestion.questionNumber;
      const isObtainedScoreChanged =
        parseFloat(editForm.obtainedScore) !==
        currentEditingQuestion.obtainedScore;
      const isMaxScoreChanged =
        parseFloat(editForm.maxScore) !== currentEditingQuestion.maxScore;
      const isFeedbackChanged =
        editForm.feedback !== currentEditingQuestion.feedback;
      const isManualReviewChanged =
        editForm.isManualReview !==
        (currentEditingQuestion.isManualReview || false);

      setIsSubmitEnabled(
        isQuestionNumberChanged ||
          isObtainedScoreChanged ||
          isMaxScoreChanged ||
          isFeedbackChanged ||
          isManualReviewChanged
      );
    }
  }, [editForm, currentEditingQuestion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentEditingQuestion || !currentEditingQuestion._id) return;

    setUpdateLoading(true);
    try {
      const feedbackText =
        editForm.feedback !== currentEditingQuestion.feedback
          ? `${editForm.feedback} (by Instructor)`
          : editForm.feedback;

      // Use the correct API structure: scoreId and questionId
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/update/${currentEditingQuestion.scoreId}/${currentEditingQuestion._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            questionNumber: editForm.questionNumber,
            obtainedScore: editForm.obtainedScore,
            maxScore: editForm.maxScore,
            feedback: feedbackText,
            isManualReview: editForm.isManualReview,
          }),
        }
      );

      if (!response) return;

      if (!response.ok) {
        throw new Error("Failed to update score");
      }

      // Update the local state
      setQuestionData((prevData) =>
        prevData.map((q) =>
          q.scoreId === currentEditingQuestion.scoreId &&
          q.questionNumber === currentEditingQuestion.questionNumber
            ? {
                ...q,
                questionNumber: editForm.questionNumber,
                obtainedScore: parseFloat(editForm.obtainedScore),
                maxScore: parseFloat(editForm.maxScore),
                feedback: feedbackText,
                isManualReview: editForm.isManualReview,
              }
            : q
        )
      );

      // Update allQuestions state as well
      setAllQuestions((prevData) =>
        prevData.map((q) =>
          q.scoreId === currentEditingQuestion.scoreId &&
          q.questionNumber === currentEditingQuestion.questionNumber
            ? {
                ...q,
                questionNumber: editForm.questionNumber,
                obtainedScore: parseFloat(editForm.obtainedScore),
                maxScore: parseFloat(editForm.maxScore),
                feedback: feedbackText,
                isManualReview: editForm.isManualReview,
              }
            : q
        )
      );

      setIsModalOpen(false);
      swalSuccess("Score updated successfully");
    } catch (err) {
      swalError("Failed to update score. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEditingQuestion(null);
  };

  // Sort question numbers numerically
  const sortQuestionNumbers = (numbers) => {
    return numbers.sort((a, b) => {
      // Extract numeric parts for comparison
      const getNumericValue = (str) => {
        const match = str.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1]) : 0;
      };

      const aNum = getNumericValue(a);
      const bNum = getNumericValue(b);

      if (aNum !== bNum) {
        return aNum - bNum;
      }

      // If numeric parts are equal, sort alphabetically
      return a.localeCompare(b);
    });
  };

  // Columns for the results table
  const columns = [
    {
      key: "studentName",
      label: "Student Name",
      render: (data) => `${data.studentFirstName} ${data.studentLastName}`,
    },
    { key: "referenceId", label: "ID" },
    {
      key: "answerPaper",
      label: "Answer Paper",
      render: (data) => (
        <button
          className="preview-btn"
          onClick={() => handlePreviewClick(data.answerpaperURI)}
        >
          <i className="fa-solid fa-eye"></i>
        </button>
      ),
      sortable: false,
    },
    { key: "studentAnswer", label: "Answer", sortable: false },
    { key: "obtainedScore", label: "Score", sortable: true },
    { key: "maxScore", label: "Max Score", sortable: true },
    { key: "feedback", label: "Feedback", sortable: false },
    {
      key: "actions",
      label: "Actions",
      render: (data) => (
        <button
          className="delete-btn"
          onClick={() => handleEditClick(data)}
          title="Edit Score"
        >
          <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={20} />
        </button>
      ),
      sortable: false,
    },
  ];

  // Fetch all questions and distinct question numbers on component mount
  useEffect(() => {
    const fetchQuestions = async () => {
      setInitialLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/score/getAllScoresByQ/${testId}`,
          {
            credentials: "include",
          }
        );
        if (!response) {
          setInitialLoading(false);
          return;
        }
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch questions");
        }
        const data = await response.json();

        // Sort question numbers
        const sortedQuestionNumbers = sortQuestionNumbers(
          data.distinctQuestionNumbers || []
        );

        setQuestionNumbers(sortedQuestionNumbers);
        setAllQuestions(data.allQuestions);
        if (sortedQuestionNumbers.length > 0) {
          setActiveQuestionNumber(sortedQuestionNumbers[0]);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch questions");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchQuestions();
  }, [serverUrl, testId]);

  // Set data for active question number
  useEffect(() => {
    setQuestionData(
      allQuestions.filter((q) => q.questionNumber === activeQuestionNumber)
    );
    setLoading(false);
  }, [activeQuestionNumber, allQuestions]);

  useEffect(() => {
    // Check for question parameter in URL and set active question
    const urlParams = new URLSearchParams(window.location.search);
    const questionParam = urlParams.get("question");

    if (questionParam && questionNumbers.length > 0) {
      // Check if the question exists in the available questions
      const questionExists = questionNumbers.includes(questionParam);
      if (questionExists) {
        setActiveQuestionNumber(questionParam);
      }
    }
  }, [questionNumbers]);

  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <Navbar links={[]} breadcrumbs={breadcrumbs} />
      <motion.div className="view-by-question">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="page-title"
        >
          View Results by Question
        </motion.h1>

        {initialLoading ? (
          <motion.div
            className="initial-loader-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
            }}
          >
            <Loader style={{ left: "0" }}/>
          </motion.div>
        ) : (
          <>
            <div className="tabs-container">
              <motion.div
                className="tabs-wrapper"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {questionNumbers.map((q) => (
                  <motion.button
                    key={q}
                    className={`tab-button ${
                      activeQuestionNumber === q ? "active" : ""
                    }`}
                    onClick={() => setActiveQuestionNumber(q)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {q}
                    {activeQuestionNumber === q && (
                      <motion.div
                        className="active-indicator"
                        layoutId="activeIndicator"
                      />
                    )}
                  </motion.button>
                ))}
              </motion.div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeQuestionNumber}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="content-container"
              >
                {loading ? (
                  <Loader />
                ) : questionData && questionData.length > 0 ? (
                  <div className="results-section">
                    <div className="question-info">
                      <h2>
                        {questionData[0]?.questionText || "Question Details"}
                      </h2>
                      <p>Total Submissions: {questionData.length}</p>
                      <p>Max Score: {questionData[0]?.maxScore || ""}</p>
                    </div>

                    <Table
                      data={questionData}
                      columns={columns}
                      caption={`Results for ${activeQuestionNumber}`}
                    />
                  </div>
                ) : (
                  <div className="no-data">
                    No results available for this question
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <Modal isOpen={isModalOpen} onClose={closeModal}>
              <div className="edit-score-modal">
                <h2>Edit Question Score and Feedback</h2>
                {fetchingQuestionDetails ? (
                  <div className="modal-loader-container">
                    <Loader />
                    <p>Loading question details...</p>
                  </div>
                ) : currentEditingQuestion ? (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="questionNumber">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Question Number:
                      </label>
                      <input
                        type="text"
                        id="questionNumber"
                        name="questionNumber"
                        value={editForm.questionNumber}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Question: {currentEditingQuestion.questionText}
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Student's Answer: {currentEditingQuestion.studentAnswer}
                      </label>
                    </div>

                    <div className="form-group">
                      <label htmlFor="obtainedScore">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Obtained Score:
                      </label>
                      <input
                        type="number"
                        id="obtainedScore"
                        name="obtainedScore"
                        value={editForm.obtainedScore}
                        onChange={handleFormChange}
                        min="0"
                        max={editForm.maxScore}
                        step="0.25"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="maxScore">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Max Score:
                      </label>
                      <input
                        type="number"
                        id="maxScore"
                        name="maxScore"
                        value={editForm.maxScore}
                        onChange={handleFormChange}
                        min="0"
                        step="0.25"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="feedback">
                        Feedback{" "}
                        <span className="optional-text">(Optional)</span>
                      </label>
                      <textarea
                        id="feedback"
                        name="feedback"
                        value={editForm.feedback}
                        onChange={handleFormChange}
                        rows="4"
                      ></textarea>
                    </div>

                    {(currentEditingQuestion.isManualReview ||
                      editForm.isManualReview) && (
                      <div className="form-group">
                        <label className="form-checkbox">
                          <input
                            type="checkbox"
                            name="isManualReview"
                            checked={editForm.isManualReview}
                            onChange={handleFormChange}
                          />
                          <span className="checkbox-label">
                            Marked for Manual Review
                          </span>
                        </label>
                        <p className="form-helper">
                          Uncheck when you've reviewed and approved this score.
                        </p>
                      </div>
                    )}

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="btn-submit"
                        disabled={!isSubmitEnabled || updateLoading}
                      >
                        {updateLoading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            </Modal>
          </>
        )}
      </motion.div>
    </>
  );
}
